import React from 'react';
import PDFHeader from './PDFHeader';
import { ItineraryItem } from '../../types/Quotation';
import FlightCard from './FlightCard';
import HotelCard from './HotelCard';
import ActivityCard from './ActivityCard';
import TransferCard from './TransferCard';

interface PDFPage3Props {
  itinerary: ItineraryItem[];
  itineraryImages: string[];
  startDate: string;
}

const formatLongDate = (d: Date) =>
  d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const DAY_MS = 86400000;

const PDFPage3: React.FC<PDFPage3Props> = ({
  itinerary,
  itineraryImages,
  startDate,
}) => {
  const base = new Date(startDate);

  return (
    <div className="pdf-page">
      <PDFHeader />

      <section className="pdf-section">
        <h2>🗓️ Day Wise Details</h2>

        {itineraryImages.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`itinerary-${i}`}
            className="itinerary-image"
          />
        ))}

        {(() => {
          const printedHotels = new Set<string>();
          return itinerary.map((day, idx) => {
            const dayDate = new Date(base.getTime() + (day.day - 1) * DAY_MS);

            return (
              <div className="day-itinerary-container" key={idx}>
              {/* Day Header */}
              <div className="day-header">
                <h3>Day {day.day}</h3>
                <span className="day-date">{formatLongDate(dayDate)}</span>
              </div>

              {/* Timeline Container */}
              <div className="timeline-container">
                {/* Transfers */}
                {day.transfers && day.transfers.length > 0 && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="transfer-section">
                        <h4>Transfers</h4>
                        {day.transfers.map((transfer, transferIndex) => (
                          <TransferCard key={`${idx}-transfer-${transferIndex}`} transfer={{ ...transfer, day: day.day }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Location Tag */}
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="location-tag">
                      <span className="location-icon">📍</span>
                      <span>{day.place.label} - {day.place.nights} Nights Stay</span>
                    </div>
                  </div>
                </div>

                {/* Hotels */}
                {day.hotels && day.hotels.length > 0 && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="hotel-section">
                        <h4>Hotel at {day.place.label}</h4>
                        {day.hotels.filter(h => {
                          const key = String((h as any).hotelId || h.name || '').toLowerCase();
                          if (!key) return false;
                          if (printedHotels.has(key)) return false;
                          printedHotels.add(key);
                          return true;
                        }).map((hotel, hotelIndex) => (
                          <HotelCard key={`${idx}-hotel-${hotelIndex}`} hotel={{ ...hotel, day: day.day }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Activities */}
                {day.activities && day.activities.length > 0 && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="activity-section">
                        {day.activities.map((activity, activityIndex) => (
                          <div key={`${idx}-activity-${activityIndex}`} className="activity-item">
                            <h4>ACTIVITY • {activity.duration_minutes ? `${Math.floor(activity.duration_minutes / 60)} Hours` : 'TBD'} • In {activity.city || day.place.label}</h4>
                            <ActivityCard activity={{ ...activity, day: day.day }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Flights */}
                {day.flights && day.flights.length > 0 && (
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="flight-section">
                        <h4>Flights</h4>
                        {day.flights.map((flight, flightIndex) => (
                          <FlightCard key={`${idx}-flight-${flightIndex}`} flight={{ ...flight, day: day.day }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }); })()}
      </section>
    </div>
  );
};

export default PDFPage3;
